import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ROUTES } from '@/lib/constants';

const navLinks = [
  { label: 'Accueil', href: ROUTES.HOME },
  {
    label: 'Services',
    href: ROUTES.SERVICES_BASE,
    children: [
      { label: 'Cuisines MDF', href: ROUTES.SERVICES.CUISINE_MDF },
      { label: 'Cuisines Modernes', href: ROUTES.SERVICES.CUISINE_MODERNE },
      { label: 'Cuisines Sur Mesure', href: ROUTES.SERVICES.CUISINE_SUR_MESURE },
      { label: 'Placards & Dressings', href: ROUTES.SERVICES.PLACARD_DRESSING },
      { label: 'Cuisines Équipées', href: ROUTES.SERVICES.CUISINE_EQUIPEE },
    ],
  },
  { label: 'Blog', href: ROUTES.BLOG },
  { label: 'Contact', href: ROUTES.CONTACT },
];

export default function Navbar() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [pathname]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white shadow-md py-2'
          : 'bg-white/95 backdrop-blur-sm shadow-sm py-3'
      }`}
    >
      <nav className="container mx-auto px-4 flex items-center justify-between">
        {/* Logo */}
        <Link to={ROUTES.HOME} className="flex items-center gap-2">
          <svg viewBox="0 0 40 40" className="w-9 h-9" aria-hidden="true">
            <rect width="40" height="40" rx="8" fill="#B45309" />
            <path d="M8 28 L20 10 L32 28 Z" fill="white" opacity="0.9" />
            <rect x="14" y="22" width="12" height="7" rx="1" fill="#B45309" />
          </svg>
          <span className="font-bold text-lg text-amber-800">
            Cuisine MDF Agadir
          </span>
        </Link>

        {/* Desktop nav */}
        <ul className="hidden md:flex items-center gap-6">
          {navLinks.map((link) =>
            link.children ? (
              <li key={link.href} className="relative group">
                <button
                  className="flex items-center gap-1 text-sm font-semibold text-gray-700 transition-colors hover:text-amber-600"
                >
                  {link.label}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <ul className="absolute top-full left-0 mt-2 w-52 bg-white shadow-xl rounded-lg py-2 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200">
                  {link.children.map((child) => (
                    <li key={child.href}>
                      <Link
                        to={child.href}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700"
                      >
                        {child.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            ) : (
              <li key={link.href}>
                <Link
                  to={link.href}
                  className={`text-sm font-semibold transition-colors hover:text-amber-600 ${
                    pathname === link.href
                      ? 'text-amber-600 border-b-2 border-amber-600 pb-0.5'
                      : 'text-gray-700'
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            )
          )}
          <li>
            <Link
              to={ROUTES.CONTACT}
              className="bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-amber-700 transition-colors"
            >
              Devis Gratuit
            </Link>
          </li>
        </ul>

        {/* Mobile burger */}
        <button
          className="md:hidden p-2 rounded-md text-gray-700"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {open ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden bg-white shadow-xl border-t">
          <ul className="py-3 px-4 space-y-1">
            {navLinks.map((link) =>
              link.children ? (
                <li key={link.href}>
                  <button
                    className="w-full text-left flex items-center justify-between py-2 text-gray-700 font-medium"
                    onClick={() => setServicesOpen(!servicesOpen)}
                  >
                    {link.label}
                    <svg className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {servicesOpen && (
                    <ul className="pl-4 space-y-1 mt-1">
                      {link.children.map((child) => (
                        <li key={child.href}>
                          <Link
                            to={child.href}
                            className="block py-2 text-sm text-gray-600 hover:text-amber-600"
                          >
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ) : (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className={`block py-2 text-gray-700 font-medium hover:text-amber-600 ${
                      pathname === link.href ? 'text-amber-600' : ''
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              )
            )}
            <li>
              <Link
                to={ROUTES.CONTACT}
                className="block mt-2 bg-amber-600 text-white text-center px-4 py-2 rounded-lg font-semibold"
              >
                Devis Gratuit
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
